import { Layer, ObjectCategory } from "@common/constants";
import { equivLayer } from "@common/utils/layer";
import { Vec } from "@common/utils/vector";
import * as PixiSound from "@pixi/sound";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { deckAt } from "../utils/roofs";
import { echoMix, ENCLOSURE_REACH, enclosureOf, INDOORS, OPEN, type EchoMix, type Place, type Reflector, roomImpulse, TOWN } from "./acousticsLogic";

/**
 * The echo of the surroundings, shared by every sound that sends to it (gunshots most, explosions, impacts a little):
 * one bus, so a hundred shots cost no more than one. Three rooms are made once, as impulse responses drawn here (no
 * files: acousticsLogic.ts):
 *  - open ground: a faint, dark roll off the land far off, nothing more (0.86: 0.84 had one outdoor room for
 *    everywhere, with discrete slaps 45 to 300 ms after each shot, at full level in the middle of a field)
 *  - between buildings: the walls' dense, short reflections and a short tail
 *  - indoors: a hard room's dense early reflections and a brighter, shorter tail
 * The mix follows where the listener is (outdoors, inside a building, underground) and, outdoors, how walled in they
 * are (`enclosureOf`: buildings, walls, containers and rocks round them, trees a little), changing smoothly. Each
 * sound's share is set by GameSound (its send: soundManager.ts), by how loud it arrives (combatSoundsLogic: roomSend,
 * to the power of the mix's `law`).
 *
 * Settings → Audio → "Gunfire echoes" (cv_sound_echoes) turns it off. It needs Web Audio (not the old HTML audio
 * fallback); anything failing here switches it off for the visit, and sounds play dry.
 */

interface Bus {
    readonly context: AudioContext
    readonly input: GainNode
    readonly open: GainNode
    readonly town: GainNode
    readonly indoor: GainNode
    readonly master: GainNode
}

interface Rooms { readonly open: AudioBuffer, readonly town: AudioBuffer, readonly indoor: AudioBuffer }

class AcousticsClass {
    private _bus?: Bus;
    /** the rooms' impulse responses: made once (60 to 90 ms of work), kept for every bus */
    private _impulses?: Rooms;
    private _broken = false;
    private _place: Place = "outdoors";
    private _enclosure = 0;
    private _mix: EchoMix = echoMix("outdoors", 0);
    private _checkedAt = 0;
    private _volume = 1;

    /** sounds may send to it now (Web Audio, the setting on, nothing broken) */
    get available(): boolean {
        if (this._broken || PixiSound.sound.useLegacy) return false;
        try {
            return GameConsole.getBuiltInCVar("cv_sound_echoes") !== false;
        } catch {
            return true;
        }
    }

    /** the bus a sound's send connects to (made the first time) */
    input(): AudioNode | undefined {
        if (!this.available) return undefined;
        try {
            return (this._bus ??= this._make()).input;
        } catch (error) {
            this._broken = true;
            console.warn("Gunfire echoes are off for this visit (they couldn't be set up):", error);
            return undefined;
        }
    }

    /** where the listener is, as last worked out (for tests and the debug overlay) */
    get place(): Place { return this._place; }
    /** how walled in they are outdoors, 0 to 1 (as last worked out) */
    get enclosure(): number { return this._enclosure; }
    /** how a sound's echo grows with its distance where the listener is now (combatSoundsLogic.roomSend) */
    get law(): number { return this._mix.law; }

    private _rooms(context: AudioContext): Rooms {
        return this._impulses ??= { open: roomImpulse(context, OPEN), town: roomImpulse(context, TOWN), indoor: roomImpulse(context, INDOORS) };
    }

    /**
     * the rooms made ahead, while nothing's going on (after the sounds are loaded, on the menu): the first gunshot of a
     * visit used to wait for them, three to five frames at the start of the first fight (the audit of 0.84's first
     * build)
     */
    prepareSoon(): void {
        const make = (): void => {
            if (!this.available || this._impulses) return;
            try {
                this._rooms(PixiSound.sound.context.audioContext);
            } catch (error) {
                this._broken = true;
                console.warn("Gunfire echoes are off for this visit (they couldn't be set up):", error);
            }
        };
        const idle = (globalThis as { requestIdleCallback?: (run: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
        if (typeof idle === "function") idle(make, { timeout: 4000 });
        else setTimeout(make, 1500);
    }

    /**
     * every sound stopped (a match left): the echoes still ringing go quiet at once, and the next sound sent here gets a
     * new bus (the same rooms). They used to ring on over the menu for a second or two
     */
    hush(): void {
        const bus = this._bus;
        if (!bus) return;
        this._bus = undefined;
        try {
            bus.master.gain.setTargetAtTime(0, bus.context.currentTime, 0.015);
            setTimeout(() => {
                try {
                    bus.master.disconnect();
                } catch { /* gone already */ }
            }, 150);
        } catch { /* silent or gone either way */ }
    }

    private _make(): Bus {
        const context = PixiSound.sound.context.audioContext;
        const input = context.createGain();
        const master = context.createGain();
        const rooms = this._rooms(context);
        const room = (buffer: AudioBuffer): GainNode => {
            const convolver = context.createConvolver();
            convolver.normalize = false;
            convolver.buffer = buffer;
            const level = context.createGain();
            input.connect(convolver).connect(level).connect(master);
            return level;
        };
        const open = room(rooms.open);
        const town = room(rooms.town);
        const indoor = room(rooms.indoor);
        // into the start of the sound library's own output chain (its compressor and the speakers)
        master.connect((PixiSound.sound.context as PixiSound.webaudio.WebAudioContext).destination);
        // (where the listener is now: a bus made again after a hush started with the last place's mix)
        this._survey();
        open.gain.value = this._mix.open;
        town.gain.value = this._mix.town;
        indoor.gain.value = this._mix.indoor;
        master.gain.value = this._volume = PixiSound.sound.volumeAll;
        return { context, input, open, town, indoor, master };
    }

    /** at every update from the server (the game's tick, 40 a second): the master volume, and (four times a second) where the listener is */
    update(): void {
        const bus = this._bus;
        try {
            const now = performance.now();
            // (only while the echo is on: off, on the old audio, or broken, nothing sends to it: the review of 0.86)
            if (this.available && now - this._checkedAt >= 250) {
                this._checkedAt = now;
                // (worked out even with no bus yet: a sound's send takes its law from it)
                const before = this._mix;
                this._survey();
                if (bus && (before.open !== this._mix.open || before.town !== this._mix.town || before.indoor !== this._mix.indoor)) {
                    bus.open.gain.setTargetAtTime(this._mix.open, bus.context.currentTime, 0.3);
                    bus.town.gain.setTargetAtTime(this._mix.town, bus.context.currentTime, 0.3);
                    bus.indoor.gain.setTargetAtTime(this._mix.indoor, bus.context.currentTime, 0.3);
                }
            }
            if (!bus) return;
            const volume = PixiSound.sound.volumeAll * (this.available ? 1 : 0);
            if (volume !== this._volume) {
                this._volume = volume;
                bus.master.gain.setTargetAtTime(volume, bus.context.currentTime, 0.05);
            }
        } catch (error) {
            this._broken = true;
            console.warn("Gunfire echoes are off for this visit:", error);
            try {
                if (bus) bus.master.gain.value = 0;
            } catch { /* it's silent or gone either way */ }
        }
    }

    /** where the listener is and, out of doors, how walled in: the mix for it (in steps, so small moves change nothing) */
    private _survey(): void {
        this._place = this._where();
        this._enclosure = this._place === "outdoors" ? Math.round(this._walledIn() * 10) / 10 : 0;
        this._mix = echoMix(this._place, this._enclosure);
    }

    /** under the ground, inside a building (under its roof), or out in the open */
    private _where(): Place {
        const player = Game.activePlayer;
        if (!player) return "outdoors";
        if (player.layer <= Layer.ToBasement) return "underground";
        // (on a roof's deck, out in the open: 1.08)
        if (deckAt(player.layer, player.position) !== undefined) return "outdoors";
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            // (a building on this floor, standing, and a room, not a canopy over a door: 0.85 took any roof at all, and the
            // blocks' entrance canopies, 7 by 15, passed for rooms until the review of 0.86)
            if (building.dead && !building.definition.hasDamagedCeiling) continue;
            if (building.definition.noCeilingScopeEffect) continue;
            if (!equivLayer({ layer: building.layer, definition: building.definition }, player)) continue;
            const roof = building.ceilingHitbox;
            if (!roof?.isPointInside(player.position)) continue;
            const { min, max } = roof.toRectangle();
            if ((max.x - min.x) * (max.y - min.y) >= 80) return "indoors";
        }
        return "outdoors";
    }

    /**
     * how walled in the listener is out of doors (acousticsLogic.enclosureOf): the buildings standing round them (their
     * roofs' outline: a building's walls), walls, containers and big rocks, and trees a little (a wood's echo is soft)
     */
    private _walledIn(): number {
        const player = Game.activePlayer;
        if (!player) return 0;
        const origin = player.position;
        const near = (x: number, y: number, size: number): boolean =>
            Math.abs(x - origin.x) < ENCLOSURE_REACH + size && Math.abs(y - origin.y) < ENCLOSURE_REACH + size;
        const reflectors: Reflector[] = [];
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            if (building.dead && !building.definition.hasDamagedCeiling) continue;
            // (a canopy over a door is no wall)
            if (building.definition.noCeilingScopeEffect) continue;
            if (!equivLayer({ layer: building.layer, definition: building.definition }, player)) continue;
            const roof = building.ceilingHitbox;
            if (!roof) continue;
            const { min, max } = roof.toRectangle();
            if ((max.x - min.x) * (max.y - min.y) < 80) continue;
            const size = Math.max(max.x - min.x, max.y - min.y);
            if (!near((min.x + max.x) / 2, (min.y + max.y) / 2, size)) continue;
            reflectors.push({ hitbox: roof, weight: 1 });
        }
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            if (obstacle.dead || !obstacle.hitbox) continue;
            if (!equivLayer({ layer: obstacle.layer, definition: obstacle.definition }, player)) continue;
            const definition = obstacle.definition;
            const { min, max } = obstacle.hitbox.toRectangle();
            const width = max.x - min.x, height = max.y - min.y;
            if (!near(obstacle.position.x, obstacle.position.y, Math.max(width, height))) continue;
            let weight = 0;
            if (definition.isWall) weight = 1;
            else if (definition.material === "tree") weight = 0.35;
            else if ((definition.material === "stone" || definition.material === "metal_heavy" || definition.material === "metal_light") && width * height >= 24) weight = 0.8;
            if (weight > 0) reflectors.push({ hitbox: obstacle.hitbox, weight });
        }
        return enclosureOf(Vec.clone(origin), reflectors);
    }
}

export const Acoustics = new AcousticsClass();
